/**
 * Tela de Residências.
 *
 * Papel: mostra, de cima para baixo, o cabeçalho "Residências", o
 * `MenuBoaVista`, um `BlocoDeLancamentos` por casa de bloco e, só quando tem
 * linha, o bloco "Ambas / não se aplica"; e abre a caixa de lançamento.
 *
 * Como usar: os blocos só são montados para quem alcança `ver` em
 * `residencias`. Quem chegou só pelas chaves do menu (`manutencoes-boa-vista`,
 * `calendario-boa-vista`) vê o cabeçalho e o menu, e a consulta de lançamentos
 * nem sai: `useLancamentosResidencia` recebe falso, inclusive enquanto o acesso
 * ainda está sendo consultado. "+ Novo lançamento", "editar" e "remover" só
 * existem para quem alcança `editar`.
 *
 * Os títulos dos blocos vêm da listagem. Enquanto ela não chega, há um bloco
 * só, sem cabeçalho, com "Carregando…"; se ela falha sem nada na tela, o mesmo
 * bloco diz "Não foi possível carregar." com "Tentar de novo". Uma releitura que
 * falha com a tabela já na tela mantém os blocos: a TanStack Query guarda o
 * `data` anterior, e a página olha o `data` antes do erro.
 *
 * Remover pergunta pelo `confirm()` nativo, com o texto da origem; a falha da
 * API vira `alert()`, e o hook lê a listagem de novo nos dois casos.
 */
import { type LancamentoResidencia, acessoAlcanca } from '@concierge/shared';
import { useState } from 'react';

import { useAcesso } from '@/shared/hooks';
import { CabecalhoDoModulo } from '@/shared/ui/cabecalho-do-modulo';
import { Vazio } from '@/shared/ui/vazio';
import { type Bloco, separarCasas, separarPorBloco, subtituloDoBloco } from '../casas';
import { BlocoDeLancamentos } from '../components/bloco-de-lancamentos';
import { FormularioLancamentoResidencia } from '../components/formulario-lancamento-residencia';
import { MenuBoaVista } from '../components/menu-boa-vista';
import {
  useExcluirLancamentoResidencia,
  useLancamentosResidencia,
} from '../hooks/usar-residencias';
import { ROTULO_SEM_RESIDENCIA } from '../rotulos';

/** O que a caixa de lançamento mostra, ou que ela está fechada. */
type Caixa =
  | { modo: 'fechada' }
  | { modo: 'novo'; bloco: Bloco }
  | { modo: 'editar'; lancamento: LancamentoResidencia; bloco: Bloco };

/** As ações que um bloco recebe; vazias para quem não alcança `editar`. */
interface AcoesDoBloco {
  onNovo?: () => void;
  onEditar?: (lancamento: LancamentoResidencia) => void;
  onRemover?: (lancamento: LancamentoResidencia) => void;
  removendo?: boolean;
}

/** O bloco "Ambas / não se aplica", de onde o lançamento novo nasce sem casa. */
const BLOCO_AMBAS: Bloco = { tipo: 'ambas' };

/**
 * Página de Residências.
 *
 * @returns O cabeçalho, o menu da Fazenda Boa Vista e, para quem alcança `ver`
 *   em `residencias`, os blocos de lançamento e a caixa quando aberta.
 */
export function PaginaResidencias() {
  const acesso = useAcesso();
  const podeVer =
    acesso.data !== undefined && acessoAlcanca(acesso.data, 'residencias', 'ver');
  const podeEditar =
    acesso.data !== undefined && acessoAlcanca(acesso.data, 'residencias', 'editar');
  const consulta = useLancamentosResidencia(podeVer);
  const excluir = useExcluirLancamentoResidencia();
  const [caixa, setCaixa] = useState<Caixa>({ modo: 'fechada' });

  const dados = consulta.data;
  const casas = dados === undefined ? null : separarCasas(dados.casas);
  const blocos =
    dados === undefined || casas === null ? null : separarPorBloco(dados.lancamentos, casas);

  /**
   * Pergunta e remove um lançamento.
   *
   * @param lancamento - O lançamento da linha onde "remover" foi clicado.
   */
  async function handleRemover(lancamento: LancamentoResidencia) {
    if (!window.confirm('Remover este lançamento?')) return;

    try {
      await excluir.mutateAsync(lancamento.id);
    } catch {
      window.alert('Não foi possível remover o lançamento.');
    }
  }

  /** Fecha a caixa, depois de salvar ou de cancelar. */
  function handleFecharCaixa() {
    setCaixa({ modo: 'fechada' });
  }

  /**
   * As ações de um bloco, que abrem a caixa com o bloco de onde vieram.
   *
   * @param bloco - O bloco que recebe as ações.
   * @returns As três ações para quem edita; nenhuma para quem só vê.
   */
  function acoesDoBloco(bloco: Bloco): AcoesDoBloco {
    if (!podeEditar) return {};

    return {
      onNovo: () => setCaixa({ modo: 'novo', bloco }),
      onEditar: (lancamento) => setCaixa({ modo: 'editar', lancamento, bloco }),
      onRemover: (lancamento) => void handleRemover(lancamento),
      removendo: excluir.isPending,
    };
  }

  return (
    <div>
      <CabecalhoDoModulo titulo="Residências" />

      <MenuBoaVista />

      {podeVer && casas !== null && blocos !== null && (
        <>
          {casas.casasDeBloco.map((casa) => (
            <BlocoDeLancamentos
              key={casa.id}
              titulo={casa.nome}
              subtitulo={subtituloDoBloco(casa)}
              lancamentos={blocos.porCasa.get(casa.id) ?? []}
              {...acoesDoBloco({ tipo: 'casa', casa })}
            />
          ))}

          {blocos.ambas.length > 0 && (
            <BlocoDeLancamentos
              titulo={ROTULO_SEM_RESIDENCIA}
              lancamentos={blocos.ambas}
              {...acoesDoBloco(BLOCO_AMBAS)}
            />
          )}
        </>
      )}

      {podeVer && dados === undefined && (
        <section className="mb-9">
          {consulta.isError ? (
            <Vazio
              mensagem="Não foi possível carregar."
              rotuloAcao="Tentar de novo"
              onAcao={() => void consulta.refetch()}
            />
          ) : (
            <Vazio mensagem="Carregando…" />
          )}
        </section>
      )}

      {caixa.modo !== 'fechada' && casas !== null && (
        <FormularioLancamentoResidencia
          key={caixa.modo === 'novo' ? 'novo' : caixa.lancamento.id}
          lancamento={caixa.modo === 'novo' ? null : caixa.lancamento}
          bloco={caixa.bloco}
          casas={casas}
          onSalvo={handleFecharCaixa}
          onCancelar={handleFecharCaixa}
        />
      )}
    </div>
  );
}
